import { Box } from '@chakra-ui/react';
import { forwardRef } from 'react';
import { useToken } from '../../../hooks';

import { BBSpinnerDefaultProps, type BBSpinnerProps } from './BBSpinner.types';

const spinKeyframes = `
  @keyframes bb-spinner-spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const sizeMap = {
  xs: 24,
  sm: 40,
  md: 56,
  lg: 72,
  xl: 96,
};

const BBSpinner = forwardRef<HTMLDivElement, BBSpinnerProps>(
  ({ size = 'md', color = 'red.500', trackColor = 'gray.800', speed = 1.2, ...props }, ref) => {
    const [resolvedColor, resolvedTrackColor] = useToken('colors', [color, trackColor]);

    const spinnerSize = sizeMap[size] || sizeMap.md;
    const radius = (spinnerSize - 8) / 2;
    const center = spinnerSize / 2;

    return (
      <>
        <style>{spinKeyframes}</style>
        <Box
          ref={ref}
          display="inline-block"
          position="relative"
          width={`${spinnerSize}px`}
          height={`${spinnerSize}px`}
          css={{
            animation: `bb-spinner-spin ${speed}s cubic-bezier(0.4, 0, 0.2, 1) infinite`,
          }}
          {...props}
        >
          <svg
            viewBox={`0 0 ${spinnerSize} ${spinnerSize}`}
            xmlns="http://www.w3.org/2000/svg"
            role="presentation"
            aria-hidden="true"
            style={{
              width: '100%',
              height: '100%',
              transform: 'rotate(-90deg)',
            }}
          >
            <circle
              cx={center}
              cy={center}
              r={radius}
              fill="none"
              stroke={resolvedTrackColor}
              strokeWidth="4"
            />
            <circle
              cx={center}
              cy={center}
              r={radius}
              fill="none"
              stroke={resolvedColor}
              strokeWidth="4"
              strokeDasharray="25 126"
              strokeDashoffset="0"
              strokeLinecap="round"
            />
          </svg>
        </Box>
      </>
    );
  }
);

BBSpinner.displayName = 'BBSpinner';
BBSpinner.defaultProps = BBSpinnerDefaultProps;

export { BBSpinner };
